import React, { useEffect, useState } from 'react';
import { MemoIcon } from '@revfluence/fresh-icons/regular/esm';
import { DatePickerWithRange } from '@frontend/app/refresh-components/DateRangePicker';
import { ValueWithPeriodInput } from '@frontend/app/refresh-components/ValueWithPeriodInput';
import { cn } from '@frontend/shadcn/lib/utils';
import { Input } from '@frontend/shadcn/components/ui/input';
import { Textarea } from '@frontend/shadcn/components/ui/textarea';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@frontend/shadcn/components/ui/hover-card';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { ProgramTimePeriod } from '@frontend/app/types/globalTypes';
import { useCampaign } from '../CampaignContext';
import { TProject } from '../../types';
import { ImageIconSelector } from '../components/ImageIconSelector';
import { SectionCardCollapsable } from '../components/SectionCardCollapsable';
import { getCampaignIconName } from '../utils';

interface Props {
  project?: TProject;
}

export const AboutSectionV2: React.FC<Props> = () => {
  const {
    templateName,
    campaignName,
    campaignDescription,
    startDate,
    endDate,
    budget,
    budgetTimePeriod,
    salesGoal,
    salesGoalTimePeriod,
    creatorCount,
    creatorsTimePeriod,
    setDates,
    setBudget,
    setCampaignName,
    setCampaignDescription,
    setSalesGoal,
    setCreatorCount,
    campaignImage,
    campaignIcon,
    setCampaignImage,
    setCampaignImageFile,
    setCampaignIcon,
    currentStep,
    programId
  } = useCampaign();

  const isEditMode = !!programId;

  useEffect(() => {
    if (!campaignIcon && !campaignImage) {
      setCampaignIcon(getCampaignIconName(templateName));
    }
  }, [campaignIcon, campaignImage, templateName, setCampaignIcon]);

  const [showDescription, setShowDescription] = useState(!!campaignDescription);

  const handleCampaignNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCampaignName(value);
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCampaignDescription(e.target.value);
  };

  const toggleDescriptionField = () => {
    setShowDescription(!showDescription);
  };

  const handleImageUrlChange = (url: string) => {
    setCampaignImage(url);
  };

  const handleImageChange = (file: File | null) => {
    setCampaignImageFile(file);
  };

  const handleIconChange = (icon: string | null) => {
    setCampaignIcon(icon);
  };

  return (
    <div className={cn(
      'transition-opacity duration-200',
      currentStep !== 2 && 'opacity-50 pointer-events-none'
    )}
    >
      <SectionCardCollapsable
        icon={<MemoIcon className="text-grey-5" />}
        title="About"
        subtitle="Provide more information about your campaign."
        stepNumber={isEditMode ? undefined : 2}
        totalSteps={isEditMode ? undefined : 2}
        isCompleted={!!campaignName}
        defaultExpanded={currentStep === 2}
      >
        <div className="flex flex-col gap-6">
          {/* Two column layout for form fields and image selector */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left column - Campaign details */}
            <div className="flex flex-col gap-2">
              {/* Campaign Name */}
              <div className="flex flex-col gap-2">
                <label className="font-medium text-xs text-grey-6" htmlFor="campaign-name">Campaign Name</label>
                <Input
                  placeholder="Enter campaign name"
                  value={campaignName}
                  onChange={handleCampaignNameChange}
                  className="border-[#E5E7EB]"
                  id="campaign-name"
                />
              </div>

              {/* Description (Optional) */}
              <div>
                {!showDescription ? (
                  <button
                    className="text-grey-6 flex items-center gap-1"
                    onClick={toggleDescriptionField}
                  >
                    + <span className="underline">Add Description</span>
                  </button>
                ) : (
                  <div className="flex flex-col gap-2">
                    <label className="font-medium text-xs text-grey-6">Description</label>
                    <Textarea
                      placeholder="Campaign Description"
                      value={campaignDescription}
                      onChange={handleDescriptionChange}
                      rows={3}
                      className="border-[#E5E7EB]"
                    />
                  </div>
                )}
              </div>

              {/* Active Dates */}
              <div className="flex flex-col gap-2 mt-2">
                <div className="flex items-center gap-2">
                  <label className="font-medium text-xs text-grey-6">Active Dates</label>
                  <HoverCard>
                    <HoverCardTrigger asChild>
                      <FontAwesomeIcon icon={faCircleInfo} className="text-grey-3 cursor-pointer" />
                    </HoverCardTrigger>
                    <HoverCardContent className="w-80">
                      <div className="flex flex-col gap-1">
                        <h4 className="font-medium">Active Dates</h4>
                        <p className="text-sm text-muted-foreground">Campaign Start Date is set to today's date by default. If applicable, configure when this campaign starts and ends.</p>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                </div>
                <DatePickerWithRange
                  startDate={startDate || new Date()}
                  endDate={endDate}
                  onRangeChange={({ from, to }) => setDates(from, to)}
                />
              </div>
            </div>

            {/* Right column - Image/Icon selector */}
            <div className="flex justify-end items-start pt-0">
              <ImageIconSelector
                imageUrl={campaignImage || null}
                iconName={campaignIcon || null}
                onImageChange={handleImageChange}
                onImageUrlChange={handleImageUrlChange}
                onIconChange={handleIconChange}
              />
            </div>
          </div>

          {/* Budget, Sales Goal, Active Collaborators */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Budget */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <label className="font-medium text-xs text-grey-6">Budget</label>
                <HoverCard>
                  <HoverCardTrigger asChild>
                    <FontAwesomeIcon icon={faCircleInfo} className="text-grey-3 cursor-pointer" />
                  </HoverCardTrigger>
                  <HoverCardContent className="w-80">
                    <div className="flex flex-col gap-1">
                      <h4 className="font-medium">Budget</h4>
                      <p className="text-sm text-muted-foreground">Master Budget Account is linked by default. You can edit the connected budget account after the campaign is created.</p>
                    </div>
                  </HoverCardContent>
                </HoverCard>
              </div>
              <ValueWithPeriodInput
                value={budget}
                period={budgetTimePeriod || ProgramTimePeriod.PER_MONTH}
                onValueChange={setBudget}
              />
            </div>

            {/* Sales Goal */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <label className="font-medium text-xs text-grey-6">Sales Goal</label>
                <HoverCard>
                  <HoverCardTrigger asChild>
                    <FontAwesomeIcon icon={faCircleInfo} className="text-grey-3 cursor-pointer" />
                  </HoverCardTrigger>
                  <HoverCardContent className="w-80">
                    <div className="flex flex-col gap-1">
                      <h4 className="font-medium">Sales Goal</h4>
                      <p className="text-sm text-muted-foreground">Set the target sales number you aim to achieve in this campaign.</p>
                    </div>
                  </HoverCardContent>
                </HoverCard>
              </div>
              <ValueWithPeriodInput
                value={salesGoal}
                period={salesGoalTimePeriod || ProgramTimePeriod.TOTAL}
                onValueChange={setSalesGoal}
              />
            </div>

            {/* Active Collaborators Goal */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <label className="font-medium text-xs text-grey-6">Active Collaborators Goal</label>
                <HoverCard>
                  <HoverCardTrigger asChild>
                    <FontAwesomeIcon icon={faCircleInfo} className="text-grey-3 cursor-pointer" />
                  </HoverCardTrigger>
                  <HoverCardContent className="w-80">
                    <div className="flex flex-col gap-1">
                      <h4 className="font-medium">Member Goal</h4>
                      <p className="text-sm text-muted-foreground">Set the target number of creators you aim to collaborate with in this campaign.</p>
                    </div>
                  </HoverCardContent>
                </HoverCard>
              </div>
              <ValueWithPeriodInput
                value={creatorCount}
                period={creatorsTimePeriod || ProgramTimePeriod.PER_MONTH}
                onValueChange={setCreatorCount}
              />
            </div>
          </div>
        </div>
      </SectionCardCollapsable>
    </div>
  );
};
